import { useEffect, useState } from 'react';

import { bytes as formatoBytes } from '@haus/core';

import { Boton } from '../componentes/Boton.js';
import { Aviso, BarraAccion, Cabecera, Chip, Contenido, Pantalla } from '../componentes/estructura.js';
import { purgarLimpiezasCompletas, type EnCola } from '../datos/db.js';
import { useApp } from '../estado/app.js';
import css from './comun.module.css';

/**
 * 08 · Cola de subida.
 *
 * Transparencia total sobre qué se ha enviado y qué no. Es la pantalla que
 * hace que la empleada pueda irse a casa tranquila —o saber que no puede
 * todavía—, y por eso no maquilla nada: si algo ha fallado dos veces, lo dice.
 *
 * El pie con el almacenamiento usa la **cuota real** que da el navegador y no
 * los "2 GB" del handoff (decisión D9): 2 GB es una suposición que en un móvil
 * lleno es falsa y en uno vacío es pesimista.
 */
export function PantallaCola() {
    const { t, idioma, cola, resumenCola, atras, hayRed, ajustes, cambiarAjustes, capacidades, dia } =
        useApp();
    const [items, setItems] = useState<EnCola[]>([]);

    useEffect(() => {
        let vivo = true;
        const refrescar = (): void => {
            void cola.items().then((i) => {
                if (vivo) {
                    setItems(i);
                }
            });
        };
        refrescar();
        const quitar = cola.escuchar(refrescar);
        const id = setInterval(refrescar, 2000);

        return () => {
            vivo = false;
            quitar();
            clearInterval(id);
        };
    }, [cola]);

    // Al entrar aquí se aprovecha para tirar lo ya subido. No se hace al
    // confirmar cada clip para no borrar mientras la empleada mira la lista y
    // ve desaparecer cosas bajo el dedo.
    useEffect(() => {
        void purgarLimpiezasCompletas();
    }, []);

    const pendientes = items.filter((i) => i.estado !== 'hecha');
    const bytesPendientes = pendientes.reduce((n, i) => n + i.bytes, 0);
    const alm = resumenCola?.almacenamiento;

    // Los dos umbrales del aviso de almacenamiento, que los pone oficina y
    // viajan con el dia. Los valores de reserva son los mismos que trae la
    // base en 0007, y hacen falta: la app tiene que poder decidir antes de
    // haber hablado nunca con el servidor.
    const avisoPct = dia?.ajustes?.aviso_almacenamiento_pct ?? 70;
    const avisoMax = (dia?.ajustes?.aviso_almacenamiento_mb_max ?? 1536) * 1024 * 1024;

    return (
        <Pantalla>
            {!hayRed && <Aviso banda>{t('comun.sin_conexion')}</Aviso>}

            <Cabecera
                titulo={t('cola.titulo')}
                subtitulo={
                    pendientes.length === 0
                        ? t('cola.resumen_vacia')
                        : t('cola.resumen', {
                              n: pendientes.length,
                              tamano: formatoBytes(bytesPendientes, idioma),
                          })
                }
                alVolver={atras}
            />

            <Contenido>
                {resumenCola?.motivo_parada === 'esperando_wifi' && (
                    <Aviso tono="info">{t('cola.esperando_wifi')}</Aviso>
                )}
                {resumenCola?.motivo_parada === 'sin_red' && <Aviso tono="info">{t('cola.esperando_red')}</Aviso>}
                {resumenCola?.trabajando === true && !capacidades.subidaEnSegundoPlano && (
                    <Aviso tono="info">{t('cola.pantalla_encendida')}</Aviso>
                )}

                {items.map((i) => {
                    const actual = resumenCola?.actual?.id === i.id;
                    const pct = actual ? (resumenCola?.actual?.pct ?? 0) : 0;

                    return (
                        <div key={i.id} className={css.fila} style={i.estado === 'fallida' ? { borderColor: 'var(--warn-line)' } : undefined}>
                            <div className={css.filaTextos}>
                                <span className={css.filaTitulo}>
                                    {i.apartamento} · {i.estancia}
                                </span>

                                {i.estado === 'hecha' && (
                                    <span className={css.filaSub}>{formatoBytes(i.bytes, idioma)}</span>
                                )}

                                {actual && (
                                    <>
                                        <span className="mono" style={{ color: 'var(--accent-ink)' }}>
                                            {t('cola.subiendo', {
                                                pct,
                                                enviado: formatoBytes(resumenCola?.actual?.enviados ?? 0, idioma),
                                                total: formatoBytes(i.bytes, idioma),
                                            })}
                                        </span>
                                        <div className={css.barraProgreso}>
                                            <div className={css.barraProgresoRelleno} style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className={css.filaSub}>{t('cola.reanudable')}</span>
                                    </>
                                )}

                                {i.estado === 'fallida' && (
                                    <span style={{ color: 'var(--warn-ink)', fontSize: 'var(--txt-terciario)', fontWeight: 700 }}>
                                        {t('cola.fallida', { n: i.intentos })} · {i.ultimo_error}
                                    </span>
                                )}
                            </div>

                            {i.estado === 'hecha' && <Chip tono="ok">✓</Chip>}
                            {i.estado === 'pendiente' && !actual && <Chip>{t('cola.en_cola')}</Chip>}
                            {i.estado === 'fallida' && (
                                <Boton variante="texto" onClick={() => void cola.reintentar(i.id)}>
                                    {t('cola.reintentar_ahora')}
                                </Boton>
                            )}
                        </div>
                    );
                })}

                {alm !== undefined && (
                    <p className="mono" style={{ textAlign: 'center', color: 'var(--ink-faint)' }}>
                        {t('cola.almacenamiento', {
                            usado: formatoBytes(alm.usado, idioma),
                            limite: alm.cuota === null ? '?' : formatoBytes(alm.cuota, idioma),
                        })}
                    </p>
                )}

                {/* El aviso de que el movil se esta quedando sin sitio.

                    El 70 estaba ESCRITO AQUI, al lado de un ajuste que decia
                    70 (`aviso_almacenamiento_pct`, desde 0007) y que no leia
                    nadie. Es el mismo caso que la barra de disco del pie del
                    panel, que se pintaba verde fija mientras el correo saltaba
                    con otro umbral: dos numeros para un concepto acaban con la
                    pantalla diciendo una cosa y el sistema haciendo otra.

                    Y son DOS umbrales porque uno solo no vale (decision D5):
                    el porcentaje no salta nunca cuando el navegador informa de
                    una cuota enorme, y el tope absoluto salta siempre en un
                    movil de 16 GB casi lleno. Avisa el primero que se cumpla. */}
                {alm !== undefined && (alm.pct > avisoPct || alm.usado > avisoMax) && (
                    <Aviso>{t('cola.almacenamiento_aviso')}</Aviso>
                )}
            </Contenido>

            <BarraAccion>
                {/* `aria-pressed` porque esto es un conmutador, no una acción.

                    Su estado se veía solo en el color y en un `✓` pegado al
                    texto: un lector de pantalla leía «Subir solo con WiFi» las
                    dos veces, encendido y apagado, y eso en la pantalla que
                    decide si la prueba del día sale del móvil o se queda
                    esperando un WiFi. Con `aria-pressed` el estado es del
                    control y no del adorno. */}
                <Boton
                    aria-pressed={ajustes.solo_wifi}
                    variante={ajustes.solo_wifi ? 'primario' : 'secundario'}
                    onClick={() => void cambiarAjustes({ solo_wifi: !ajustes.solo_wifi })}
                >
                    {t('cola.solo_wifi')}
                    {ajustes.solo_wifi ? ' ✓' : ''}
                </Boton>
                {pendientes.length > 0 && (
                    <Boton variante="cuadrado" aria-label={t('comun.reintentar')} onClick={() => void cola.reintentar()}>
                        ↻
                    </Boton>
                )}
            </BarraAccion>
        </Pantalla>
    );
}
